import { ReactComponent as LockIcon } from '@vkontakte/icons/src/svg/20/lock_outline_20.svg';
import browser from 'Cloud/browser';
import config from 'Cloud/config';
import classNames from 'clsx';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_TABLET_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { abSharingLinkFakedoor } from 'reactApp/appHelpers/featuresHelpers';
import { enabledPublicDownloadLimits, tooltipLimitSharing } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import { showWeblinkListFileSharing } from 'reactApp/appHelpers/featuresHelpers/features/tabsFolderSharingDesign';
import { isThemedSharedModalItem } from 'reactApp/appHelpers/featuresHelpers/features/themedPublic';
import { isWeblinkDisabledBlockByDefault } from 'reactApp/appHelpers/featuresHelpers/features/weblink-disabled-block-by-default';
import { unPublishHelper } from 'reactApp/appHelpers/publishHelper';
import { SharingBizDisabledWeblinkBlock } from 'reactApp/components/SharingWindow/blocks/SharingBizDisabledWeblinkBlock/SharingBizDisabledWeblinkBlock';
import { SharingDisabledWeblinkBlock } from 'reactApp/components/SharingWindow/blocks/SharingDisabledWeblinkBlock/SharingDisabledWeblinkBlock';
import { renderUnpublishWeblinkDialog } from 'reactApp/components/SharingWindow/blocks/Weblink/SharingWeblink.helpers';
import { SHARING_VARIANT } from 'reactApp/components/SharingWindow/blocks/Weblink/WeblinkList/SharingNewWeblinkList.types';
import { useOnboardingEnabled } from 'reactApp/components/SharingWindow/hooks/onboardingEnabled.hook';
import { useWeblinkOpened } from 'reactApp/components/SharingWindow/hooks/useWeblinkOpened.hook';
import { sendGa } from 'reactApp/components/SharingWindow/Sharing.helpers';
import { EFrom } from 'reactApp/components/SharingWindow/Sharing.types';
import { useIsWindowIntegrationSmall } from 'reactApp/hooks/useIsWindowIntegrationSmall';
import { getEditorsFor, getIsFileForCoEditing } from 'reactApp/modules/editor/editor.selectors';
import { getFeatureEditingNoneditableFormats, getFeaturePublicDomestic } from 'reactApp/modules/features/features.selectors';
import { XrayItemType } from 'reactApp/modules/home/home.types';
import { publishRequest } from 'reactApp/modules/modifying/modifying.actions';
import { loadPaidInfoRequest } from 'reactApp/modules/paidInfo/paidInfo.module';
import { getPublicSharingInfo } from 'reactApp/modules/public/public.actions';
import { getPublicLimits, getPublicPin } from 'reactApp/modules/public/public.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getItemById } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { CopyWeblink } from 'reactApp/ui/CopyWeblink/CopyWeblink';
import { sendXray } from 'reactApp/utils/ga';
import { getPublicUrl } from 'reactApp/utils/urlHelper';

import styles from './SharingNewWeblinkTab.css';
import type { IProps } from './SharingWeblink.types';
import { SharingNewWeblinkList } from './WeblinkList/SharingNewWeblinkList';

const IS_FREE_B2B_BIZ_EDITORS_USER = config.get('FREE_B2B_BIZ_EDITORS_USER');

/* eslint-disable complexity */
// eslint-disable-next-line max-lines-per-function
export const SharingWeblinkIndividualAccessTab = memo(function SharingNewWeblink(props: IProps): ReactElement | null {
    const {
        item,
        from,
        isSharedOrMouted,
        isPublic,
        isPhone,
        isOwn,
        onUnpublish,
        // tempexp_17779-start
        onboardingEnabled,
        // tempexp_17779-end
        variant = SHARING_VARIANT.folders,
        folderName,
        formProps,
        onCloseModal,
        onOpenAccess,
    } = props;

    const dispatch = useDispatch();

    const isPublished = Boolean(item?.weblink);
    const { isWeblinkOpened, setWeblinkOpened, isWeblinkInfoLoading } = useWeblinkOpened(from, isPublished);

    const itemStorage = useSelector(getCurrentStorage);
    const storage = item?.storage || itemStorage;
    const isAlbum = from === EFrom.ALBUMS || storage === EStorageType.albums;
    const isStock = itemStorage === EStorageType.stock;
    const isBizUser = useSelector(UserSelectors.isBizUser);
    const editingNoneEditableFormatsFeature = useSelector((state: RootState) => {
        if (!item) {
            return;
        }
        return getFeatureEditingNoneditableFormats(state, {
            file: {
                ext: typeof item === 'object' && 'ext' in item ? item.ext : undefined,
                size: typeof item === 'object' && 'size' in item ? item.size : undefined,
            },
            storage,
        });
    });
    const isConvertFileForCoEditing = useSelector((state: RootState) => getIsFileForCoEditing(state, { ...item, ext: convertExt }));
    const isFileForCoEditing = useSelector((state: RootState) => getIsFileForCoEditing(state, item));
    const isEditable = !!useSelector((state: RootState) => getEditorsFor(state, convertExt ? { ...item, ext: convertExt } : item))?.length;
    const parent = useSelector((state: RootState) => getItemById(state, item?.parent || ''));
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const isCorpUser = useSelector(UserSelectors.isCorpUser);
    const isDomesticFeatureEnabled = useSelector(getFeaturePublicDomestic);

    const { onboardingAllUsers, onboardingPaidUsers } = useOnboardingEnabled(onboardingEnabled);

    const convertExt = editingNoneEditableFormatsFeature?.ext || '';
    const isFolder = item?.isFolder;
    const isDocumentForEdit =
        (item?.kind === 'document' && IS_FREE_B2B_BIZ_EDITORS_USER) ||
        isFileForCoEditing ||
        (isConvertFileForCoEditing && !(IS_TABLET_BROWSER || browser.isIpadOs()) && isEditable);
    const isReadOnlyItem = !!item?.isReadOnly;
    // Исключаем стоки, у которых них нет публичной ссылки
    const publicUrl = (isPublished && getPublicUrl(item, isAlbum)) || '';
    const isReadOnlyParent = !!(parent && 'isReadOnly' in parent && parent?.isReadOnly);
    const isReadOnlyProperty = !isReadOnlyItem && isSharedOrMouted.isMounted ? isReadOnlyParent : isReadOnlyItem;
    const isReadOnly = isFolder ? isReadOnlyProperty : isReadOnlyProperty || !isDocumentForEdit;
    const canShareForDomestic = isBizUser || isCorpUser;
    const pin = useSelector((state: RootState) => getPublicPin(state, item?.weblink || ''));
    const downloadLimits = useSelector((state: RootState) => getPublicLimits(state, item?.weblink || ''));
    const showSharingTooltipPromo =
        !onboardingAllUsers && enabledPublicDownloadLimits && tooltipLimitSharing && !isPaidUser && isWeblinkOpened && !!downloadLimits;
    const showDomesticSettings =
        (!isAlbum || !isStock) &&
        ((canShareForDomestic && Boolean(isDomesticFeatureEnabled)) || showWeblinkListFileSharing || variant === SHARING_VARIANT.files) &&
        isOwn;

    const xrayItemType = useMemo(() => {
        if (isAlbum) {
            return XrayItemType.album;
        } else if (isFolder) {
            return XrayItemType.folder;
        }
        return XrayItemType.file;
    }, [isAlbum, isFolder]);

    const isWindowSmall = useIsWindowIntegrationSmall();

    const onCloseAccessClick = useCallback(() => {
        const onCloseAccess = () => {
            sendGa('unpublish');
            if (storage === EStorageType.attaches) {
                sendGa('attaches-unpublish');
            }

            if (!item) {
                return;
            }

            unPublishHelper({ items: [item] });

            setWeblinkOpened(false);
            onUnpublish?.();
        };

        renderUnpublishWeblinkDialog({ onActionClick: onCloseAccess, variant });
    }, [variant, storage, item, setWeblinkOpened, onUnpublish]);

    // tempexp_17779-start
    const onOpenAccessClick = useCallback(() => {
        if (!item) {
            return;
        }

        sendGa('publish');
        dispatch(publishRequest({ item }));

        onOpenAccess?.();
    }, [dispatch, item, onOpenAccess]);
    // tempexp_17779-end

    const renderWeblinkDisabledBlock = useCallback(() => {
        if (isWeblinkDisabledBlockByDefault && showDomesticSettings && item) {
            return (
                <SharingBizDisabledWeblinkBlock
                    item={item}
                    storage={storage}
                    canShareForDomestic={canShareForDomestic}
                    showSharingTooltipPromo={showSharingTooltipPromo}
                    downloadLimits={downloadLimits}
                    isBizUser={isBizUser}
                    formProps={formProps}
                    pin={pin}
                    onCloseModal={onCloseModal}
                    variant={variant}
                    xrayItemType={xrayItemType}
                    isPhone={isPhone}
                    isPublic={isPublic}
                    onOpenAccessClick={onOpenAccessClick}
                    onCloseAccessClick={onCloseAccessClick}
                />
            );
        }

        return (
            <SharingDisabledWeblinkBlock isPhone={isPhone} isPublic={isPublic} onOpenAccessClick={onOpenAccessClick} variant={variant} />
        );
    }, [
        canShareForDomestic,
        downloadLimits,
        formProps,
        isBizUser,
        isPhone,
        isPublic,
        item,
        onCloseAccessClick,
        onCloseModal,
        onOpenAccessClick,
        pin,
        showDomesticSettings,
        showSharingTooltipPromo,
        storage,
        variant,
        xrayItemType,
    ]);

    useEffect(() => {
        if (item?.weblink) {
            dispatch(getPublicSharingInfo({ public_id: item.weblink }));
            // todo: заменить на новую ручку.
            dispatch(loadPaidInfoRequest());
        }
    }, [dispatch, isPaidUser, item?.weblink]);

    useEffect(() => {
        if (item?.weblink) {
            setWeblinkOpened(true);
        } else if (!publicUrl) {
            setWeblinkOpened(false);
        }
    }, [item?.weblink, publicUrl, setWeblinkOpened]);

    useEffect(() => {
        const userType = isBizUser ? 'biz' : 'b2c';

        sendXray(['sharing-weblink-info-mount', xrayItemType, userType]);
    }, []);

    if (!isWeblinkOpened && !isWeblinkInfoLoading) {
        return renderWeblinkDisabledBlock();
    }
    // tempexp_17779-end

    if (!item) {
        onCloseModal();
        return null;
    }

    return (
        <div data-qa-section="weblink">
            <div
                className={classNames(styles.root, {
                    [styles.root_phone]: isPhone,
                    [styles.root_tutoria]: isWindowSmall,
                    [styles.onboardingPaid]: onboardingPaidUsers,
                })}
            >
                <div className={styles.personalAccessContainer}>
                    <SharingNewWeblinkList
                        xrayItemType={xrayItemType}
                        item={item}
                        from={from}
                        isPhone={isPhone}
                        isReadOnly={isReadOnly}
                        variant={variant}
                    />
                </div>

                {/* tempexp_17779-start */}
                <div className={styles.copyWeblink}>
                    <CopyWeblink
                        type="sharingTabs"
                        publicUrl={publicUrl}
                        autoCopy={!isPhone}
                        isPhone={isPhone}
                        redesignIcon={!isFolder}
                        isFolder={isFolder}
                        isDownloadable={item?.weblinkDownloadable}
                        weblinkAccessRights={item?.weblinkAccessRights}
                        folderName={folderName}
                        weblinkDomestic={item?.weblinkDomestic}
                        // tempexp_17341-next-line
                        tooltipEnabledByExp={abSharingLinkFakedoor === 'b' && !isThemedSharedModalItem}
                        item={item}
                        // tempexp_17341-next-line
                        showOtherSharingTooltipPromo={!isPhone}
                        fileName={item?.name}
                    />
                    <Button
                        qaId="sharing-close-access"
                        className={styles.btnContainer}
                        theme="base"
                        middle
                        onClick={onCloseAccessClick}
                        sizeMode={isPhone ? ButtonSizeMode.big : ButtonSizeMode.middle}
                    >
                        <div className={styles.closeAccessButton}>
                            <LockIcon width={20} height={20} />
                            <span>Закрыть доступ{isFolder && ' по ссылке'}</span>
                        </div>
                    </Button>
                </div>
                {/* tempexp_17779-end */}
            </div>
        </div>
    );
});
